/**
 * useShadowObservation.ts — MER Algo Footprints — Phase 6 foundation (P1-Q4 / G-CLIENT-4C)
 *
 * Position-9 shadow-observation dispatch. Realizes the banked authority
 * D-GCLIENT4C-4 through D-GCLIENT4C-9 (ADR section 49).
 *
 * The hook owns auth eligibility, the current canonical pair read, pure predicate
 * invocation, authenticated-lifecycle duplicate suppression, request dispatch, and
 * failure containment. It owns no UI, writes no Zustand state, persists nothing,
 * and creates no response state.
 *
 * Capability state is deliberately not read: the observed operand must stay
 * independent of the capability data used to derive its counterpart, and
 * `NO_VALID_OVERLAY` must never block an observation. This hook therefore has no
 * ordering dependency on `useCapabilityHydration` in either direction.
 *
 * There is no automatic retry for any outcome, no `Retry-After` scheduling, no
 * timer, no polling, no backoff, and no request queue. A failed attempt is terminal
 * for that observation key in that authenticated lifecycle.
 */

import { useEffect, useRef } from 'react';
import { useAuth } from '../auth/AuthContext';
import { useStore } from '../store';
import {
  buildShadowObservation,
  observationKey,
} from '../shared/capabilities/gClient4CShadowObservation';

const SHADOW_OBSERVATION_ENDPOINT = '/api/shadow-observations';

export function useShadowObservation(): void {
  const { status } = useAuth();
  // Exactly the canonical selection context. Never the active symbol, never
  // symbolData[symbol].interval, never capability or provider state.
  const horizon = useStore(s => s.horizon);
  const canonicalTimeframe = useStore(s => s.canonicalTimeframe);

  // The last observation key attempted in the current authenticated lifecycle.
  // Process-local and non-durable: it survives rerenders, resets when the
  // authenticated lifecycle ends, and never reaches storage of any kind.
  const lastAttemptedKey = useRef<string | null>(null);

  useEffect(() => {
    if (status !== 'authenticated') {
      // Leaving the authenticated lifecycle re-arms the next lifecycle's initial
      // observation, even when the canonical pair is unchanged.
      lastAttemptedKey.current = null;
      return;
    }

    // A non-canonical pair yields no observation and no dispatch. It is never
    // coerced, aliased, or defaulted.
    const observation = buildShadowObservation(horizon, canonicalTimeframe);
    if (!observation) return;

    const key = observationKey(observation);
    if (lastAttemptedKey.current === key) return;

    // Marked BEFORE the network call, so a rerender while the request is still in
    // flight cannot resend the identical key. Dedupe is never success-dependent.
    lastAttemptedKey.current = key;

    // A distinct key dispatches immediately even if an earlier request is still
    // pending: each transition is an independent observation, with no queue, no
    // serialization, and no cancellation. Late completions are inert because this
    // hook keeps no response state and touches no business state on any outcome.
    void fetch(SHADOW_OBSERVATION_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(observation),
    }).catch(() => undefined);
  }, [status, horizon, canonicalTimeframe]);
}
